/**
 * schemas/config/bootstrap.ts
 * ──────────────────────────────────────────────────────────────────────────
 * GET /v1/config/bootstrap — the ONE call the shell makes on load.
 *
 * It answers everything the app needs before it can render a single pixel:
 * which console this user gets, which gates are unmet, and the whole
 * configuration surface (policy, rules, option lists, categories). Doing this
 * in one round trip rather than five is why the shell has no cascade of
 * loading spinners.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { z } from 'zod';
import { gateStateSchema } from './gates';
import { policySchema } from './policy';
import { rulesSchema } from './rules';
import { optionSchema } from './options';
import { categorySchema } from './categories';

/**
 * The three consoles. Derived by the API from the caller's scopes — this app
 * never picks a role, and the prototype's role switcher is not shipped.
 */
export const CONSOLES = ['finance', 'approver', 'custodian'] as const;
export type ConsoleRole = (typeof CONSOLES)[number];

// Declared in its own leaf module — see the note in gates.ts about the import
// cycle it used to create with policy.ts.
export { gateStateSchema, type GateStateResponse } from './gates';

export const bootstrapSchema = z.object({
  console: z.enum(CONSOLES),
  gates: gateStateSchema,
  policy: policySchema,
  rules: rulesSchema,
  options: z.array(optionSchema),
  categories: z.array(categorySchema),
});
export type Bootstrap = z.infer<typeof bootstrapSchema>;
